import detectEthereumProvider from '@metamask/detect-provider';
import { NFT } from '@thirdweb-dev/sdk';
import Torus from '@toruslabs/torus-embed';
import Cookies from 'universal-cookie/es6';
import Web3 from 'web3';

import { ILootboxListingResult } from './models/lootbox/i-lootbox-trade.interface';
import { ITradeRequest } from './models/trade/i-trade-request.interface';
import { IWalletCurrencyDetails } from './models/wallet-currency.interface';
import { IAuctionHouseTransactionConfiguration } from './models/web3Singleton/i-auction-house-transaction-configuration.interface';
import { ILootboxListingTransactionConfiguration } from './models/web3Singleton/i-lootbox-listing-transaction-configuration.interface';
import { ILootboxPackTransactionConfiguration } from './models/web3Singleton/i-lootbox-pack-transaction-configuration.interface';
import { ITransactionConfiguration } from './models/web3Singleton/i-transaction-configuration.interface';

import { CookiesKeys } from './enums/cookies-keys.enum';

import { ROYALTY_WITHDRAWN_WEI_MOCK } from './mocks/royalty-points.mocks';

import { isRoyaltyMocksEnabled } from './utils/dev-royalty-mocks.utils';
import { StakingContractType } from 'enums/staking-contract-type.enum';

import { AuctionHouseService } from './services/web3/auction-house.service';
import { CashierService } from './services/web3/cashier.service';
import { Erc20Service } from './services/web3/erc20.service';
import { Erc1155Service } from './services/web3/erc1155.service';
import { LootboxService } from './services/web3/lootbox.service';
import { SigningService } from './services/web3/signing.service';
import { StakingService } from './services/web3/staking.service';
import { IPoolUserInfo } from './services/web3/staking.service';
import { TradeService } from './services/web3/trade.service';
import { IWeb3Services, Web3ServiceFactory } from './services/web3/web3-service-factory';

import { IEnvironmentAddNetwork } from './hooks/useEnv';

import { getActiveNetworkOverride } from './constants/runtime-network';

import { removeAuthenticationCookies } from './utils/remove-authentication-cookies.util';

import { Web3Providers } from './reducers/web3.reducer';

class Web3Singleton {
  private onProviderChange: (provider: Web3Providers) => void = () => { };
  private static _instance: Web3Singleton = new Web3Singleton();
  private web3: Web3 | null = null;
  private readOnlyWeb3: Web3 | null = null;
  private ethereum: any = null;
  private currentProvider: Web3Providers = Web3Providers.none;
  private torus: Torus | null = null;
  private NO_WEB3_MESSAGE = 'No Web3 provider detected';
  private isMetamaskUsed = null;

  // Service factory for centralized service creation
  private serviceFactory: Web3ServiceFactory | null = null;

  // All Web3 service instances
  private services: IWeb3Services | null = null;

  // Read-only service factory and services (for public RPC access without wallet)
  private readOnlyServiceFactory: Web3ServiceFactory | null = null;
  private readOnlyServices: IWeb3Services | null = null;

  constructor() {
    if (Web3Singleton._instance) {
      throw new Error('Error: Instantiation failed: Use SingletonClass.getInstance() instead of new.');
    }

    Web3Singleton._instance = this;
  }

  static getInstance = (): Web3Singleton => {
    return Web3Singleton._instance;
  };

  registerOnProviderChange = (onProviderChange: (provider: Web3Providers) => void): void => {
    this.onProviderChange = onProviderChange;
  };

  getWeb3 = (): Web3 | null => {
    return this.web3;
  };

  /**
   * Returns the read-only Web3 instance for public RPC calls.
   * Use this when you need to read blockchain data without a wallet connection.
   */
  getReadOnlyWeb3 = (): Web3 | null => {
    return this.readOnlyWeb3;
  };

  /**
   * Initializes a read-only Web3 provider using a public RPC URL.
   * This allows reading blockchain data without MetaMask/Torus.
   * @param rpcUrl - The public RPC URL to connect to
   */
  initializeReadOnlyProvider = (rpcUrl: string): void => {
    if (this.readOnlyWeb3) {
      return; // Already initialized
    }

    try {
      this.readOnlyWeb3 = new Web3(new Web3.providers.HttpProvider(rpcUrl));
      this.initializeReadOnlyServices();
    } catch (e) {
      console.error('Failed to initialize read-only provider:', e);
    }
  };

  getAddress = async (): Promise<string | null> => {
    const address = await this.web3?.eth.getAccounts();

    return address?.length ? address[0] : '';
  };

  getChainId = async (): Promise<number> => {
    if (!this.web3) {
      return Promise.resolve(0);
    }

    return await this.web3.eth.getChainId();
  };

  isWithProvider = async (): Promise<boolean> => {
    if (this.currentProvider === Web3Providers.none) {
      return false;
    }

    return true;
  };

  isOnCorrectNetwork = async (): Promise<boolean> => {
    if (this.currentProvider === Web3Providers.none) {
      return false;
    }

    const chainId = await this.getChainId();
    const currentChainIdHex = this.web3?.utils.toHex(chainId);
    const expectedNetworkId =
      getActiveNetworkOverride()?.networkId ?? process.env.REACT_APP_NETWORK_ID!;
    const correctNetworkIdHex = this.web3?.utils.toHex(+expectedNetworkId);

    return correctNetworkIdHex === currentChainIdHex;
  };

  getCurrencyBalance = async (currencyAddress: string, accountAddress: string): Promise<string> => {
    const service = this.services?.erc20Service ?? this.readOnlyServices?.erc20Service;
    if (!service) throw new Error(this.NO_WEB3_MESSAGE);
    return await service.getBalance(currencyAddress, accountAddress);
  };

  createStateHashForSale = async (abi: any[], assetAddress: string, tokenId: string): Promise<string> => {
    return await this.getTradeService().createStateHashForSale(abi, assetAddress, tokenId);
  };

  changeChain = async (addNetworkSettings: IEnvironmentAddNetwork): Promise<void> => {
    if (this.currentProvider === Web3Providers.torus) {
      const network = JSON.parse(process.env.REACT_APP_TORUS!);

      this.torus!.setProvider({
        ...network,
      });

      return;
    }

    const override = getActiveNetworkOverride();
    const targetChainIdHex = override
      ? override.chainIdHex || '0x' + Number(override.networkId).toString(16)
      : this.web3?.utils.toHex(process.env.REACT_APP_NETWORK_ID!);

    try {
      await (window as any).ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [addNetworkSettings],
      });

      await this.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: targetChainIdHex }],
      });
    } catch (error) {
      throw new Error((error as any).message);
    }
  };

  // sign
  createSignature = async (toSignature: string, walletAddress: string): Promise<string> => {
    return await this.getSigningService().createSignature(toSignature, walletAddress);
  };

  signMethod = async (walletAddress: string, message: any): Promise<string> => {
    return await this.getSigningService().signTradeOffer(walletAddress, message, this.getChainId);
  };

  signMarketOfferBid = async (tradeRequest: ITradeRequest, accountAddress: string): Promise<string> => {
    return await this.getSigningService().signMarketBid(tradeRequest, accountAddress, this.getChainId);
  };

  acceptMarketOfferBid = async (configuration: ITransactionConfiguration): Promise<void> => {
    await this.getTradeService().acceptBidOffer(configuration);
  };

  auctionHouseBid = async (configuration: IAuctionHouseTransactionConfiguration): Promise<void> => {
    await this.getAuctionHouseService().bid(configuration);
  };

  auctionHouseStableBid = async (configuration: IAuctionHouseTransactionConfiguration): Promise<void> => {
    await this.getAuctionHouseService().stableBid(configuration);
  };

  // permission
  checkPermissionToERC1155 = async (walletAddress: string, assetAddress: string): Promise<boolean> => {
    return await this.getErc1155Service().checkPermission(walletAddress, assetAddress);
  };

  grantPermissionToAssetERC1155 = async (walletAddress: string, assetAddress: string): Promise<void> => {
    await this.getErc1155Service().grantPermission(walletAddress, assetAddress);
  };

  checkPermissionToCurrency = async (
    assetAddress: string,
    walletAddress: string,
    currency: Pick<IWalletCurrencyDetails, 'address'>,
    currencyAmount: number
  ): Promise<boolean> => {
    return await this.getErc20Service().checkPermission(assetAddress, walletAddress, currency, currencyAmount);
  };

  grantCurrencyPermissionService = async (
    contractAddress: string,
    currency: Pick<IWalletCurrencyDetails, 'address' | 'decimal'>,
    accountAddress: string
  ): Promise<void> => {
    await this.getErc20Service().grantPermission(contractAddress, currency, accountAddress);
  };

  grantCurrencyPermissionServiceWithAmount = async (
    contractAddress: string,
    currency: IWalletCurrencyDetails,
    accountAddress: string,
    amount: string
  ): Promise<void> => {
    await this.getErc20Service().grantPermissionWithAmount(contractAddress, currency, accountAddress, amount);
  };

  grantCurrencyPermissionServiceWrapper = async (
    contractAddress: string,
    currency: IWalletCurrencyDetails,
    accountAddress: string,
    amount: string
  ): Promise<void> => {
    await this.getErc20Service().grantPermissionWrapper(contractAddress, currency, accountAddress, amount);
  };

  transferToken = async (
    tokenAddress: string,
    toAddress: string,
    amount: string,
    fromAddress: string
  ): Promise<string> => {
    return await this.getErc20Service().transfer(tokenAddress, toAddress, amount, fromAddress);
  };

  // initialization
  initializeProviders = async (): Promise<Web3Providers> => {
    try {
      const addNetworkSettings = JSON.parse(process.env.REACT_APP_ADD_NETWORK_SETTINGS!);
      this.initializeReadOnlyProvider(addNetworkSettings.rpcUrls[0]);
    } catch (e) {}

    this.isMetamaskUsed = await detectEthereumProvider({ mustBeMetaMask: true });
    let isTorusUsed = !!new Cookies().get(CookiesKeys.isTorusUsed);

    if (isTorusUsed) {
      try {
        await this.initializeTorus();
        await this.loginTorus();

        return this.currentProvider;
      } catch (e) {
        isTorusUsed = false;

        removeAuthenticationCookies();
        this.logoutTorus();
      }
    }

    if (this.isMetamaskUsed) {
      await this.initializeMetamask();
      this.onProviderChange(Web3Providers.metamask);

      return this.currentProvider;
    }

    this.onProviderChange(Web3Providers.none);
    return this.currentProvider;
  };

  initializeMetamask = async (): Promise<void> => {
    if (this.torus) {
      this.torus.hideTorusButton();
    }

    const providers = (window as any).ethereum.providers;

    if (Array.isArray(providers)) {
      const metamask = Array.from((window as any).ethereum.providers).find(
        (provider: any) => provider.isMetaMask
      ) as any;

      this.web3 = new Web3(metamask);
      this.ethereum = metamask;
    } else {
      this.web3 = new Web3(Web3.givenProvider);
      this.ethereum = await detectEthereumProvider({ mustBeMetaMask: true });
    }

    this.initializeServices();
    this.changeProvider(Web3Providers.metamask);
  };

  initializeTorus = async (): Promise<void> => {
    if (this.torus) {
      this.torus.showTorusButton();
    }

    const torus = new Torus();
    const isProd = process.env.NODE_ENV !== 'development';

    await torus.init({
      buildEnv: isProd ? 'production' : 'testing',
      enableLogging: isProd ? false : true,
      network: JSON.parse(process.env.REACT_APP_TORUS!),
    });

    this.torus = torus;
    this.web3 = new Web3(torus.provider as any);
    this.ethereum = torus.ethereum;
    this.initializeServices();
    this.changeProvider(Web3Providers.torus);
  };

  /**
   * Initializes service instances when web3 is available
   * Uses ServiceFactory pattern for centralized service creation and dependency management
   * @private
   */
  private initializeServices(): void {
    if (this.web3) {
      this.serviceFactory = new Web3ServiceFactory(this.web3, this.ethereum, this.torus);
      this.services = this.serviceFactory.createAllServices();
    }
  }

  /**
   * Initializes read-only service instances for public RPC access
   * These services can only read data, not sign transactions
   * @private
   */
  private initializeReadOnlyServices(): void {
    if (this.readOnlyWeb3) {
      this.readOnlyServiceFactory = new Web3ServiceFactory(this.readOnlyWeb3);
      this.readOnlyServices = this.readOnlyServiceFactory.createAllServices();
    }
  }

  /**
   * Gets a service instance with proper null checking
   * @private
   */
  private getTradeService(): TradeService {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.tradeService;
  }

  private getErc20Service(): Erc20Service {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.erc20Service;
  }

  private getAuctionHouseService(): AuctionHouseService {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.auctionHouseService;
  }

  private getLootboxService(): LootboxService {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.lootboxService;
  }

  private getErc1155Service(): Erc1155Service {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.erc1155Service;
  }

  private getCashierService(): CashierService {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.cashierService;
  }

  private getSigningService(): SigningService {
    if (!this.services?.signingService) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.signingService;
  }

  private getStakingService(): StakingService {
    if (!this.services) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }
    return this.services.stakingService;
  }

  /**
   * Returns a staking service for read-only blockchain queries.
   *
   * Selection rules:
   *  1. If a dev network override is active, always use the read-only service
   *     so reads go through the RPC URL matching the override (otherwise the
   *     wallet-connected web3 instance — bound to whatever chain MetaMask is
   *     on — would read the wrong chain and return empty bytes).
   *  2. Otherwise prefer the wallet-connected service when available.
   *  3. Fall back to the read-only service.
   */
  private getReadStakingService(): StakingService {
    const hasOverride = !!getActiveNetworkOverride();

    if (hasOverride && this.readOnlyServices?.stakingService) {
      return this.readOnlyServices.stakingService;
    }
    if (this.services?.stakingService) {
      return this.services.stakingService;
    }
    if (this.readOnlyServices?.stakingService) {
      return this.readOnlyServices.stakingService;
    }
    throw new Error(this.NO_WEB3_MESSAGE);
  }

  /**
   * Returns the read-only staking service for public data access.
   * Use this when you need to read staking data without a wallet connection.
   * @throws Error if read-only provider is not initialized
   */
  getReadOnlyStakingService(): StakingService {
    if (!this.readOnlyServices) {
      throw new Error('Read-only provider not initialized. Call initializeReadOnlyProvider() first.');
    }
    return this.readOnlyServices.stakingService;
  }

  /**
   * Checks if the read-only provider is initialized and ready
   */
  hasReadOnlyProvider = (): boolean => {
    return this.readOnlyWeb3 !== null && this.readOnlyServices !== null;
  };

  /**
   * Returns an effective staking service - prefers wallet-connected service,
   * falls back to read-only service if no wallet is connected.
   * @param preferReadOnly - If true, returns read-only service even if wallet is connected
   */
  getEffectiveStakingService(preferReadOnly = false): StakingService | null {
    if (!preferReadOnly && this.services?.stakingService) {
      return this.services.stakingService;
    }
    if (this.readOnlyServices?.stakingService) {
      return this.readOnlyServices.stakingService;
    }
    return null;
  }

  resetProviders(): void {
    if (this.currentProvider !== Web3Providers.torus) {
      return;
    }

    this.web3 = null;
    this.ethereum = null;
    this.torus = null;

    if (this.isMetamaskUsed) {
      this.initializeMetamask();
    }
  }

  // logout
  logout = async (): Promise<void> => {
    switch (this.currentProvider) {
      case Web3Providers.torus:
        this.logoutTorus();
        break;
      case Web3Providers.metamask:
        break;
      default:
        break;
    }
  };

  // login
  loginTorus = async (): Promise<void> => {
    await this.torus!.login();
    await this.requestAccounts();
  };

  loginMetamask = async (): Promise<void> => {
    await this.requestAccounts();
  };

  requestAccounts = async (): Promise<string[]> => {
    if (!this.ethereum?.request) {
      throw new Error(this.NO_WEB3_MESSAGE);
    }

    return await this.ethereum.request({ method: 'eth_requestAccounts' });
  };

  // listeners
  onChainChange = async (handleAccountChange: (chain: string) => void) => {
    this.ethereum?.addListener('chainChanged', handleAccountChange as any);
  };

  removeOnChainChange = async (handleAccountChange: (chain: string) => void) => {
    this.ethereum?.removeListener('chainChanged', handleAccountChange as any);
  };

  onAccountChange = async (handleAccountChange: (walletAddress: string[]) => void) => {
    this.ethereum?.addListener('accountsChanged', handleAccountChange as any);
  };

  removeOnAccountChange = async (handleAccountChange: (walletAddress: string[]) => void) => {
    this.ethereum?.removeListener('accountsChanged', handleAccountChange as any);
  };

  // market
  isSignatureUsed = async (signatureHash: string): Promise<boolean> => {
    return await this.getTradeService().isSignatureUsed(signatureHash);
  };

  purchesAsset = async (configuration: ITransactionConfiguration): Promise<void> => {
    await this.getTradeService().purchaseAsset(configuration);
  };

  passiveIncomeWithdrawn = async (walletAddress: string): Promise<string> => {
    // Dev-only, and inert unless the royalty mock is switched on in a non-production build —
    // `isRoyaltyMocksEnabled` is the same guard every other seam in that layer uses. Mocked here,
    // at the one method both call sites share, so `useSelectedWalletRoyalties` and
    // `ProfileRoyalties` stay unaware that a mock exists. See `ROYALTY_WITHDRAWN_WEI_MOCK` for why
    // the claimable balance is unreachable without this half.
    if (isRoyaltyMocksEnabled()) return ROYALTY_WITHDRAWN_WEI_MOCK;

    return await this.getCashierService().getWithdrawn(walletAddress);
  };

  passiveIncomeVoucherPayment = async (
    walletAddress: string,
    amount: number,
    voucherPaymentSignature: string
  ): Promise<any> => {
    return await this.getCashierService().voucherPayment(walletAddress, amount, voucherPaymentSignature);
  };

  getTransactionStatus = async (transactionHash: string): Promise<boolean> => {
    return this.web3!.eth.getTransactionReceipt(transactionHash).then(
      (transactionReceipt) => transactionReceipt?.status
    );
  };

  // lootboxes
  getLootboxesOwned = async (walletAddress: string, packContractAddress: string): Promise<NFT[]> => {
    return await this.getLootboxService().getLootboxesOwned(walletAddress, packContractAddress);
  };

  getMarketplaceLootboxesListings = async (): Promise<ILootboxListingResult[]> => {
    return await this.getLootboxService().getMarketplaceLootboxesListings();
  };

  purchaseMarketplaceLootboxListing = async (configuration: ILootboxListingTransactionConfiguration): Promise<void> => {
    return await this.getLootboxService().purchaseMarketplaceLootboxListing(configuration);
  };

  getLootboxListings = async (): Promise<ILootboxListingResult[]> => {
    return await this.getLootboxService().getLootboxListings();
  };

  purchaseLootboxListing = async (configuration: ILootboxListingTransactionConfiguration): Promise<void> => {
    await this.getLootboxService().purchaseLootboxListing(configuration);
  };

  checkLootboxPrice = async (listingId: string): Promise<string> => {
    return await this.getLootboxService().checkLootboxPrice(listingId);
  };

  openLootbox = async (configuration: ILootboxPackTransactionConfiguration): Promise<any> => {
    return await this.getLootboxService().openLootbox(configuration);
  };

  // Staking
  checkAPY = async (poolId: number, stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkAPY(poolId, stakingContractType);
  };

  checkStakingFee = async (poolId: number, stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkStakingFee(poolId, stakingContractType);
  };

  checkDefaultMinimumDeposit = async (stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkDefaultMinimumDeposit(stakingContractType);
  };

  checkGeneratedInterestLastDayFor = async (walletAddress: string, poolId: number): Promise<string> => {
    return await this.getStakingService().checkGeneratedInterestLastDayFor(walletAddress, poolId);
  };

  checkGeneratedRewardLastDayFor = async (walletAddress: string, poolId: number): Promise<string> => {
    return await this.getStakingService().checkGeneratedRewardLastDayFor(walletAddress, poolId);
  };

  checkIfInterestClaimOpen = async (poolId: number): Promise<boolean> => {
    return await this.getStakingService().checkIfInterestClaimOpen(poolId);
  };

  checkIfRewardClaimOpen = async (poolId: number): Promise<boolean> => {
    return await this.getStakingService().checkIfRewardClaimOpen(poolId);
  };

  checkStakedAmountBy = async (
    walletAddress: string,
    poolId: number,
    stakingContractType: StakingContractType
  ): Promise<string> => {
    return await this.getStakingService().checkStakedAmountBy(walletAddress, poolId, stakingContractType);
  };

  checkIfStakingOpen = async (poolId: number, stakingContractType: StakingContractType): Promise<boolean> => {
    return await this.getStakingService().checkIfStakingOpen(poolId, stakingContractType);
  };

  checkIfPoolEnded = async (poolId: number, stakingContractType: StakingContractType): Promise<boolean> => {
    return await this.getStakingService().checkIfPoolEnded(poolId, stakingContractType);
  };

  checkPoolType = async (poolId: number, stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkPoolType(poolId, stakingContractType);
  };

  checkStakingTarget = async (poolId: number, stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkStakingTarget(poolId, stakingContractType);
  };

  checkTotalClaimableInterestBy = async (walletAddress: string, poolId: number): Promise<string> => {
    return await this.getStakingService().checkTotalClaimableInterestBy(walletAddress, poolId);
  };

  checkTotalClaimableRewardBy = async (walletAddress: string, poolId: number): Promise<string> => {
    return await this.getStakingService().checkTotalClaimableRewardBy(walletAddress, poolId);
  };

  checkTotalStaked = async (poolId: number, stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().checkTotalStaked(poolId, stakingContractType);
  };

  claimAllInterest = async (
    walletAddress: string,
    poolId: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().claimAllInterest(walletAddress, poolId, callback);
  };

  claimAllReward = async (
    walletAddress: string,
    poolId: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().claimAllReward(walletAddress, poolId, callback);
  };

  stakeToken = async (
    walletAddress: string,
    poolId: number,
    tokenAmount: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().stakeToken(walletAddress, poolId, tokenAmount, callback);
  };

  safeStake = async (
    walletAddress: string,
    poolId: number,
    tokenAmount: string,
    forMaxFeePercentage: string,
    stakingContractType: StakingContractType,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().safeStake(
      walletAddress,
      poolId,
      tokenAmount,
      forMaxFeePercentage,
      stakingContractType,
      callback
    );
  };

  stakingToken = async (stakingContractType: StakingContractType): Promise<string> => {
    return await this.getStakingService().stakingToken(stakingContractType);
  };

  rewardToken = async (): Promise<string> => {
    return await this.getStakingService().rewardToken();
  };

  withdrawAll = async (
    walletAddress: string,
    poolId: number,
    stakingContractType: StakingContractType,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().withdrawAll(walletAddress, poolId, stakingContractType, callback);
  };

  // periodical stake
  periodicalStakingToken = async (): Promise<string> => {
    return await this.getStakingService().periodicalStakingToken();
  };

  periodicalStakingMinimumDeposit = async (): Promise<string> => {
    return await this.getStakingService().periodicalStakingMinimumDeposit();
  };

  periodicalSafeStake = async (
    walletAddress: string,
    stakingPhase: string,
    stakingPeriod: string,
    tokenAmount: string,
    expectedAPY: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().periodicalSafeStake(
      walletAddress,
      stakingPhase,
      stakingPeriod,
      tokenAmount,
      expectedAPY,
      callback
    );
  };

  getPeriodicalStakingData = async (): Promise<any> => {
    return await this.getStakingService().getPeriodicalStakingData();
  };

  periodicalStakingDepositCount = async (walletAddress: string): Promise<number> => {
    return await this.getReadStakingService().periodicalStakingDepositCount(walletAddress);
  };

  getPeriodicalStakingDepositsInRange = async (walletAddress: string, from: number, to: number): Promise<any> => {
    return await this.getReadStakingService().getPeriodicalStakingDepositsInRange(walletAddress, from, to);
  };

  periodicalStakingGetUserData = async (dataType: number, walletAddress: string): Promise<any> => {
    return await this.getReadStakingService().periodicalStakingGetUserData(dataType, walletAddress);
  };

  periodicalStakingCheckClaimableDataFor = async (walletAddress: string): Promise<any> => {
    return await this.getReadStakingService().periodicalStakingCheckClaimableDataFor(walletAddress);
  };

  periodicalStakingClaimAll = async (
    walletAddress: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().periodicalStakingClaimAll(walletAddress, callback);
  };

  periodicalStakingClaim = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().periodicalStakingClaim(walletAddress, depositNumber, callback);
  };

  periodicalStakingWithdraw = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().periodicalStakingWithdraw(walletAddress, depositNumber, callback);
  };

  // Whitelisted stake functions
  whitelistedStakingToken = async (): Promise<string> => {
    return await this.getStakingService().whitelistedStakingToken();
  };

  // Main function - fetches all data needed for UI
  checkPoolUserInfo = async (userAddress: string): Promise<IPoolUserInfo> => {
    return await this.getReadStakingService().checkPoolUserInfo(userAddress);
  };

  // Deposit functions
  getWhitelistedDepositsInRange = async (userAddress: string, fromIndex: number, toIndex: number): Promise<any> => {
    return await this.getReadStakingService().getWhitelistedDepositsInRange(userAddress, fromIndex, toIndex);
  };

  // Transaction functions - with callbacks
  whitelistedStakeToken = async (
    walletAddress: string,
    tokenAmount: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().whitelistedStakeToken(walletAddress, tokenAmount, callback);
  };

  whitelistedClaimInterest = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().whitelistedClaimInterest(walletAddress, depositNumber, callback);
  };

  whitelistedWithdrawDeposit = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().whitelistedWithdrawDeposit(walletAddress, depositNumber, callback);
  };

  whitelistedClaimAllInterest = async (
    walletAddress: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().whitelistedClaimAllInterest(walletAddress, callback);
  };

  whitelistedStakingDepositCount = async (walletAddress: string): Promise<number> => {
    return await this.getReadStakingService().whitelistedStakingDepositCount(walletAddress);
  };

  checkAllowedAmountLeftFor = async (userAddress: string): Promise<string> => {
    return await this.getStakingService().checkAllowedAmountLeftFor(userAddress);
  };

  checkIfWithdrawalOpen = async (poolId: number, stakingContractType: StakingContractType): Promise<boolean> => {
    return await this.getStakingService().checkIfWithdrawalOpen(poolId, stakingContractType);
  };

  checkWithdrawnAmountBy = async (userAddress: string): Promise<string> => {
    return await this.getStakingService().checkWithdrawnAmountBy(userAddress);
  };

  // VIP staking functions
  getVipProgramDataWithUserData = async (userAddress: string): Promise<{
    currentPhase: string;
    periods: string[];
    targets: string[][];
    apys: string[][];
    staked: string[][];
    requiredWorth: string[][];
    limits: string[][];
    remaining: string[][];
    eligibilities: boolean[][];
  }> => {
    return await this.getStakingService().getVipProgramDataWithUserData(userAddress);
  };

  vipStakingMinimumDeposit = async (): Promise<string> => {
    return await this.getStakingService().vipStakingMinimumDeposit();
  };

  vipSafeStake = async (
    walletAddress: string,
    stakingPhase: string,
    stakingPeriod: string,
    tokenAmount: string,
    expectedAPY: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().vipSafeStake(
      walletAddress,
      stakingPhase,
      stakingPeriod,
      tokenAmount,
      expectedAPY,
      callback
    );
  };

  vipStakingDepositCount = async (walletAddress: string): Promise<number> => {
    return await this.getReadStakingService().vipStakingDepositCount(walletAddress);
  };

  getVipStakingDepositsInRange = async (walletAddress: string, from: number, to: number): Promise<any> => {
    return await this.getReadStakingService().getVipStakingDepositsInRange(walletAddress, from, to);
  };

  vipStakingClaimAll = async (
    walletAddress: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().vipStakingClaimAll(walletAddress, callback);
  };

  vipStakingClaim = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().vipStakingClaim(walletAddress, depositNumber, callback);
  };

  vipStakingWithdraw = async (
    walletAddress: string,
    depositNumber: number,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<any> => {
    return await this.getStakingService().vipStakingWithdraw(walletAddress, depositNumber, callback);
  };

  // NFT Transfer
  callSafeTransferFrom = async (
    contractAddress: string,
    fromAddress: string,
    toAddress: string,
    tokenId: string,
    amount: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<void> => {
    return await this.getErc1155Service().safeTransferFrom(
      contractAddress,
      fromAddress,
      toAddress,
      tokenId,
      amount,
      callback
    );
  };

  private logoutTorus = async (): Promise<void> => {
    await this.torus?.cleanUp();

    this.resetProviders();
  };

  private changeProvider(provider: Web3Providers): void {
    this.currentProvider = provider;
    this.onProviderChange(this.currentProvider);
  }
}

export default Web3Singleton;
